import React, { useEffect, useState } from 'react';
import { BASEROW_CONFIG, getBaserowHeaders } from '../services/baserow/config';
import { RefreshCw } from 'lucide-react';

interface QuestionCounterProps {
  subtopicId: number | null;
  refreshTrigger?: number;
}

interface CountStats {
  total: number;
  remaining: number;
}

export function QuestionCounter({ subtopicId, refreshTrigger = 0 }: QuestionCounterProps) {
  const [stats, setStats] = useState<CountStats>({ total: 0, remaining: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!subtopicId) {
      setStats({ total: 0, remaining: 0 });
      setLoading(false);
      return;
    }

    async function fetchStats() {
      try {
        setLoading(true);
        setError(null);

        // Fetch total questions for this subtopic
        const totalUrl = new URL(`${BASEROW_CONFIG.BASE_URL}/rows/table/${BASEROW_CONFIG.TABLE_ID}/`);
        totalUrl.searchParams.append('user_field_names', 'true');
        totalUrl.searchParams.append('count', 'true');
        totalUrl.searchParams.append('filter__SubTopic__single_select_equal', String(subtopicId));

        // Fetch remaining (not compared) questions
        const remainingUrl = new URL(`${BASEROW_CONFIG.BASE_URL}/rows/table/${BASEROW_CONFIG.TABLE_ID}/`);
        remainingUrl.searchParams.append('user_field_names', 'true');
        remainingUrl.searchParams.append('count', 'true');
        remainingUrl.searchParams.append('filter__SubTopic__single_select_equal', String(subtopicId));
        remainingUrl.searchParams.append('filter__Compared__boolean', 'false');

        const [totalResponse, remainingResponse] = await Promise.all([
          fetch(totalUrl.toString(), { headers: getBaserowHeaders() }),
          fetch(remainingUrl.toString(), { headers: getBaserowHeaders() })
        ]);

        if (!totalResponse.ok || !remainingResponse.ok) {
          throw new Error('Failed to fetch question counts');
        }

        const totalData = await totalResponse.json();
        const remainingData = await remainingResponse.json();

        setStats({
          total: totalData.count,
          remaining: remainingData.count
        });
      } catch (err) {
        console.error('Error fetching question counts:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch counts');
      } finally {
        setLoading(false);
      }
    }

    fetchStats();
  }, [subtopicId, refreshTrigger]); // Add refreshTrigger to dependencies

  if (loading) {
    return (
      <div className="text-sm text-gray-500 animate-pulse flex items-center gap-2">
        <RefreshCw className="w-4 h-4 animate-spin" />
        Loading counts...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-500">
        Error: {error}
      </div>
    );
  }

  if (!subtopicId || stats.total === 0) {
    return null;
  }

  return (
    <div className="text-sm font-medium">
      <span className="text-blue-600">{stats.remaining}</span>
      <span className="text-gray-500">/</span>
      <span className="text-gray-600">{stats.total}</span>
      <span className="text-gray-500 ml-1">remaining</span>
    </div>
  );
}